import React, { useEffect, useState } from "react";
import { fmtIST } from "../lib/time";
import { api, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import DeleteRequestButton from "../components/DeleteRequestButton";

export default function FifoOverrides() {
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState("pending_approval");
  const [reviewOf, setReviewOf] = useState(null);   // {id, number, remark, ...row}
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();
  const isApprover = !!user?.role?.is_super_admin || user?.role?.name === "Plant Head/Admin";

  const load = async () => {
    const r = await api.get(`/fifo-override-requests?status=${tab}`);
    setRows(r.data || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const decide = async (decision) => {
    if (!reviewOf || busy) return;
    if ((decision === "reject" || decision === "send_back") && !(reviewOf.remark || "").trim()) {
      toast.error(`Reason required for ${decision === "reject" ? "Reject" : "Send Back"}`);
      return;
    }
    setBusy(true);
    try {
      // Re-fetch latest status to avoid stale state — common cause of "already processed"
      const fresh = await api.get(`/fifo-override-requests?status=pending_approval`);
      const stillPending = (fresh.data || []).find((x) => x.id === reviewOf.id);
      if (!stillPending) {
        toast.error("This request was already processed by another reviewer. Refreshing list.");
        setReviewOf(null); setBusy(false); load(); return;
      }
      if (decision === "approve") {
        await api.post(`/fifo-override-requests/${reviewOf.id}/approve`);
        toast.success("Approved & issued");
      } else if (decision === "reject") {
        await api.post(`/fifo-override-requests/${reviewOf.id}/reject`, { remark: reviewOf.remark });
        toast.success("Rejected");
      } else if (decision === "send_back") {
        await api.post(`/fifo-override-requests/${reviewOf.id}/send-back`, { remark: reviewOf.remark });
        toast.success("Sent back to Store user");
      }
      setReviewOf(null); load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  const closingDecision = (r) => {
    if (r.status === "approved") return `Approved by ${r.approved_by} · ${fmtIST(r.approved_at)}`;
    if (r.status === "rejected") return `Rejected by ${r.approved_by || r.sent_back_by} · ${fmtIST(r.approved_at)} — ${r.reject_remark || ""}`;
    if (r.status === "sent_back") return `Sent back by ${r.sent_back_by} · ${fmtIST(r.sent_back_at)} — ${r.send_back_remark || ""}`;
    return "—";
  };

  return (
    <div data-testid="fifo-overrides-page">
      <PageHeader title="FIFO Override Approvals" subtitle="Review FIFO override requests from Store users"
        helpText="When a Store user scans a newer lot/box while older stock exists, the system creates a FIFO override request. Plant Head or Super Admin reviews — Approve & Issue, Reject (cancel), or Send Back (need more justification)."
      />
      <div className="p-8 space-y-4">
        <div className="flex gap-2">
          {[
            ["pending_approval", "Pending"],
            ["approved", "Approved"],
            ["rejected", "Rejected"],
            ["sent_back", "Sent Back"],
          ].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`fifo-tab-${k}`}
              className={`px-4 py-2 text-xs uppercase tracking-widest font-bold rounded-sm border ${tab === k ? "bg-[#0D2B5E] text-white border-[#0D2B5E]" : "bg-white text-[#0D2B5E] border-[#EEEEEE]"}`}>{l}</button>
          ))}
        </div>
        <div className="bg-white border border-[#EEEEEE] rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F5F5F5] text-[10px] uppercase tracking-widest text-[#0D2B5E]/60">
              <tr>
                <th className="text-left px-4 py-2">Req#</th>
                <th className="text-left px-4 py-2">Material</th>
                <th className="text-left px-4 py-2">Newer Box (scanned)</th>
                <th className="text-left px-4 py-2">Older Lot (blocked by)</th>
                <th className="text-right px-4 py-2">Qty</th>
                <th className="text-left px-4 py-2">Reason</th>
                <th className="text-left px-4 py-2">Requested By · At</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id} className={i % 2 === 0 ? "bg-white" : "bg-[#F5F5F5]/40"} data-testid={`fifo-req-${r.number}`}>
                  <td className="px-4 py-2 font-mono text-[#1565C0] font-bold">{r.number}</td>
                  <td className="px-4 py-2"><span className="font-mono text-[#1565C0]">{r.material_code}</span> {r.material_name}</td>
                  <td className="px-4 py-2 text-xs font-mono">{r.grn_number} · L{r.lot_no} · B{r.box_no}</td>
                  <td className="px-4 py-2 text-xs font-mono text-[#E65100]">{r.older_grn_number} · L{r.older_lot_no}</td>
                  <td className="px-4 py-2 text-right font-mono font-bold">{r.issue_qty}</td>
                  <td className="px-4 py-2 text-xs italic text-[#0D2B5E]/80 max-w-xs truncate" title={r.fifo_override_reason}>{r.fifo_override_reason}</td>
                  <td className="px-4 py-2 text-xs"><div>{r.requested_by_name}</div><div className="text-[10px] tracking-widest text-[#0D2B5E]/50 font-mono">{fmtIST(r.requested_at)}</div></td>
                  <td className="px-4 py-2 text-right">
                    {tab === "pending_approval" && isApprover && (
                      <button onClick={() => setReviewOf({ ...r, remark: "" })}
                        className="text-xs bg-[#2E7D32] text-white px-3 py-1 rounded-sm uppercase tracking-wider font-bold inline-flex items-center gap-1"
                        data-testid={`fifo-review-${r.number}`}>
                        <CheckCircle2 size={12}/>Review
                      </button>
                    )}
                    {tab !== "pending_approval" && (
                      <span className="text-xs text-[#0D2B5E]/50">{closingDecision(r)}</span>
                    )}
                    <span className="inline-block align-middle ml-2"><DeleteRequestButton module="fifo_override" record={r} label={r.number} onDeleted={load}/></span>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={8} className="px-4 py-12 text-center text-sm text-[#0D2B5E]/50">No requests in this state.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {reviewOf && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" data-testid="fifo-review-dialog">
          <div className="bg-white p-6 rounded-sm w-[560px] max-h-[90vh] overflow-y-auto">
            <div className="text-sm font-bold text-[#0D2B5E] uppercase tracking-widest mb-3 flex items-center gap-2">
              <AlertTriangle size={16} className="text-[#E65100]"/> Review FIFO Override · {reviewOf.number}
            </div>
            <div className="grid grid-cols-2 gap-3 mb-3 text-xs bg-[#F5F5F5] p-3 rounded-sm">
              <div>
                <div className="text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold">Material</div>
                <div className="font-mono"><span className="text-[#1565C0] font-bold">{reviewOf.material_code}</span> {reviewOf.material_name}</div>
              </div>
              <div>
                <div className="text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold">Issue Qty</div>
                <div className="font-mono font-bold text-lg">{reviewOf.issue_qty}</div>
              </div>
              <div>
                <div className="text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold">Newer Box (scanned)</div>
                <div className="font-mono text-[#1565C0]">{reviewOf.grn_number} · L{reviewOf.lot_no} · B{reviewOf.box_no}</div>
              </div>
              <div>
                <div className="text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold">Older Lot (blocked by)</div>
                <div className="font-mono text-[#E65100]">{reviewOf.older_grn_number} · L{reviewOf.older_lot_no}</div>
              </div>
              <div className="col-span-2">
                <div className="text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold">Store User Reason</div>
                <div className="italic text-[#0D2B5E]">"{reviewOf.fifo_override_reason}"</div>
              </div>
              <div className="col-span-2">
                <div className="text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold">Requested By</div>
                <div>{reviewOf.requested_by_name} · {fmtIST(reviewOf.requested_at)}</div>
              </div>
            </div>

            <label className="block text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold mb-1">Reviewer Remark (required for Reject / Send Back)</label>
            <textarea rows={3} value={reviewOf.remark || ""}
              onChange={(e) => setReviewOf({ ...reviewOf, remark: e.target.value })}
              placeholder="e.g. Need stronger justification, or 'Older lot is QA-flagged so override permitted'"
              data-testid="fifo-review-remark"
              className="w-full border border-[#EEEEEE] px-3 py-2 text-sm rounded-sm"/>

            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setReviewOf(null)}
                className="px-4 py-2 border border-[#EEEEEE] text-sm rounded-sm">Cancel</button>
              <button disabled={busy} onClick={() => decide("reject")}
                className="px-4 py-2 bg-[#C62828] text-white text-sm rounded-sm font-semibold tracking-wider uppercase disabled:opacity-50"
                data-testid="fifo-reject-btn">Reject</button>
              <button disabled={busy} onClick={() => decide("send_back")}
                className="px-4 py-2 bg-[#E65100] text-white text-sm rounded-sm font-semibold tracking-wider uppercase disabled:opacity-50"
                data-testid="fifo-sendback-btn">Send Back</button>
              <button disabled={busy} onClick={() => decide("approve")}
                className="px-4 py-2 bg-[#2E7D32] text-white text-sm rounded-sm font-semibold tracking-wider uppercase disabled:opacity-50"
                data-testid="fifo-approve-btn">Approve & Issue</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
